import { useContext, useState, useEffect } from 'react';
import { APIContext } from '../../Contexts/APIContext';
import InputLabel from '@mui/material/InputLabel';
import Grid from '@mui/material/Grid2';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { FormContainer, useForm } from 'react-hook-form-mui';
import ValidatedTextField from '../../Utilities/Forms/ValidatedTextField';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import CreateOrganisationViewModel from './ViewModels/CreateEditOrganisationViewModel';
import DropDownViewModel from '../../Utilities/ViewModels/DropDownViewModel';
import ValidatedSelectElement from '../../Utilities/Forms/ValidatedSelectElement';
import dayjs, { Dayjs } from 'dayjs';
import Paper from '@mui/material/Paper';
import Line from '../../Utilities/Line';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import { useNavigate } from 'react-router-dom';


export default function CreateOrganisationForm() {
    const [estimateNoticeDropdown, setEstimateNoticeDropdown] = useState<DropDownViewModel[]>([]);
    const [invoiceNoticeDropdown, setInvoiceNoticeDropdown] = useState<DropDownViewModel[]>([]);
    const [invoicePayMethodDropdown, setInvoicePayMethodDropdown] = useState<DropDownViewModel[]>([]);
    const [invoicePayPeriodDropdown, setInvoicePayPeriodDropdown] = useState<DropDownViewModel[]>([]);
    const [licenseTypeDropdown, setLicenseTypeDropdown] = useState<DropDownViewModel[]>([]);
    const [locationDropdown, setLocationDropDown] = useState<DropDownViewModel[]>([]);
    const [regions, setRegions] = useState<DropDownViewModel[]>([]);
    const { post, get, showMessage, loading } = useContext(APIContext);
    const navigate = useNavigate();

    const formContext = useForm<CreateOrganisationViewModel>({
        defaultValues: new CreateOrganisationViewModel(),
    });
    const { setValue, getValues, watch } = formContext;

    useEffect(() => {
        getDropdowns();
    }, []);

    const getDropdowns = async () => {
        get('Organisation', 'GetEstimateNoticeDropdown', setEstimateNoticeDropdown);
        get('Organisation', 'GetInvoiceNoticeDropdown', setInvoiceNoticeDropdown);
        get('Organisation', 'GetInvoicePayMethodDropdown', setInvoicePayMethodDropdown);
        get('Organisation', 'GetInvoicePayPeriodDropdown', setInvoicePayPeriodDropdown);
        get('Organisation', 'GetLicenseTypeDropdown', setLicenseTypeDropdown);
        get('Organisation', 'GetLocationDropdown', setLocationDropDown);
        get("OrganisationSite", `GetRegionsDropdown`, setRegions);
    };

    const updateFunc = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        const name = event.target.name.split('-')[0];
        update(0, value, name);
    };
    const update = (siteIndex: number, value: string, fieldName: string) => {
        const currentSiteList = getValues("sites") || [];
        const updatedSiteList = [...currentSiteList];

        updatedSiteList[siteIndex] = {
            ...updatedSiteList[siteIndex],
            [fieldName]: value,
        };

        setValue("sites", updatedSiteList);
    };

    const onSuccess = (data) => {
        showMessage('success', 'Organisation Successfully Created');
        setTimeout(() => {
            navigate('/admin/manageorganisation?organisationid=' + data)
        }, 1500);
    }

    const create = async (form: CreateOrganisationViewModel) => {
        const site = form.sites.at(0);
        if (site) {
            site.administratorSite = true;
        }

        await post('organisation', 'CreateOrganisation', form, onSuccess);
    };

    const handleInputChange = (event: any) => {
        setValue("renewalDate", event);
    };

    function getAdjustedMonthName(date: Dayjs, dropdownList: DropDownViewModel[], id: string): string {
        if (dropdownList.length > 0) {
            const selected = dropdownList.find(f => f.id == id)?.label.charAt(0);
            if (selected != null) {
                const num = -parseInt(selected);
                return date.add(num, 'month').format('MMMM');
            }
        }
        return "";
    }

    const estimateNoticeId = watch("estimateNoticeId");
    const invoiceNoticeId = watch("invoiceNoticeId");
    const expiryDate = watch("expiryDate");
    const licenseTypeId = watch("licenseTypeId");
    const invalidNotices = (invoiceNoticeDropdown.find(f => f.id == invoiceNoticeId)?.label?.charAt(0) ?? 0) > (estimateNoticeDropdown.find(f => f.id == estimateNoticeId)?.label?.charAt(0) ?? 0);

    return (

        <Grid container>
            <Grid>
                <Typography variant="h4" gutterBottom>
                    Create New Organisation
                </Typography>
            </Grid>
            <Grid size={12} component={Paper}>
                <FormContainer
                    formContext={formContext}
                    onSuccess={create}
                >
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'en-gb'}>
                        <Grid container alignItems="flex-start">
                            <Grid>
                                <Typography variant="h6">Organisation Details</Typography>
                            </Grid>
                            <Line />
                            <Grid size={6} container>
                                <Grid size={6}>
                                    <ValidatedTextField
                                        label="Organisation Name"
                                        name="organisationName"
                                        required
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <ValidatedSelectElement
                                        label="License Type"
                                        name="licenseTypeId"
                                        options={licenseTypeDropdown}
                                        required
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <ValidatedSelectElement
                                        label="Location"
                                        name="locationId"
                                        options={locationDropdown}
                                        required
                                    />
                                </Grid>
                                <Grid size={6}>
                                    <DatePicker
                                        label="Expiry Date"
                                        name="renewalDate"
                                        onChange={handleInputChange}
                                        views={['day']}
                                        format="DD/MM/YYYY"
                                        sx={{ display: 'flex' }}
                                        slotProps={{ textField: { size: 'small' } }}
                                        minDate={dayjs()}
                                        
                                    />
                                </Grid>
                            </Grid>

                            {licenseTypeId !== "17b6994c-9d71-4cd1-bb79-e29d36ae36e2" &&
                                <Grid size={6} container alignItems="flex-start">
                                    <Grid size={12}>
                                        <ValidatedTextField
                                            label="Invoice Payee"
                                            name="invoicePayee"
                                            required
                                        />
                                    </Grid>

                                    <Grid size={4}>
                                        <ValidatedSelectElement
                                            label="Invoice Payment Period"
                                            name="invoicePayPeriodId"
                                            options={invoicePayPeriodDropdown}
                                            required
                                        />
                                    </Grid>

                                    <Grid size={8}>
                                        <ValidatedSelectElement
                                            label="Invoice Payment Method"
                                            name="invoicePayMethodId"
                                            options={invoicePayMethodDropdown}
                                            required
                                        />
                                    </Grid>

                                    <Grid size={4}>
                                        <ValidatedSelectElement
                                            label="Invoice Notice"
                                            name="invoiceNoticeId"
                                            options={invoiceNoticeDropdown}
                                            required
                                        />
                                    </Grid>
                                    <Grid size={8}>
                                        <TextField label="Invoice Notice Month" value={getAdjustedMonthName(dayjs(expiryDate), invoiceNoticeDropdown, invoiceNoticeId)} />
                                    </Grid>


                                    <Grid size={4}>
                                        <ValidatedSelectElement
                                            label="Estimate Notice"
                                            name="estimateNoticeId"
                                            options={estimateNoticeDropdown}
                                            required
                                        />
                                    </Grid>
                                    <Grid size={8}>
                                        <TextField label="Invoice Estimate Month" value={getAdjustedMonthName(dayjs(expiryDate), estimateNoticeDropdown, estimateNoticeId)} />
                                    </Grid>
                                    {invalidNotices &&
                                        <Grid size={12}>
                                            <Alert severity="error">Estimate Notice should be before Invoice Notice</Alert>
                                        </Grid>
                                    }
                                </Grid>
                            }
                            <Grid size={12}>
                                <Typography variant="h6">Administrator Site Details</Typography>
                            </Grid>
                            <Line />
                            <Grid size={3}>
                                <InputLabel shrink>Office Name</InputLabel>
                                <ValidatedTextField name={`officeName`} onChange={updateFunc} required />
                            </Grid>
                            <Grid size={3}>
                                <InputLabel shrink>Town</InputLabel>
                                <ValidatedTextField name={`town`} onChange={updateFunc} required />
                            </Grid>
                            <Grid size={3}>
                                <InputLabel shrink>Region</InputLabel>
                                <ValidatedSelectElement name="region" options={regions} disabled={loading} required />
                            </Grid>
                            <Grid size={3}>
                                <InputLabel shrink>Country</InputLabel>
                                <ValidatedTextField name={`country`} onChange={updateFunc} required />
                            </Grid>
                            <Grid size={3}>
                                <Button onClick={() => navigate("/admin/manageorganisations")}>Cancel</Button>
                            </Grid>
                            <Grid size={3}>
                                <Button
                                    type="submit"
                                    disabled={invalidNotices}>
                                    Create Organisation
                                </Button>
                            </Grid>
                        </Grid>
                    </LocalizationProvider>
                </FormContainer>
            </Grid>
        </Grid>

    );
}
